<template>
  <div class="card">
    <div class="card-header">
      <div class="d-flex justify-content-between">
        <h5>{{
            new Date(mass.datetime).toLocaleString("de-DE", {
              dateStyle: "medium",
              timeStyle: "short",
              timeZone: "UTC"
            })
          }}</h5>
        <!-- Use UTC as timezone, as the Django backend only stores timezone independent dates. Bit hacky, but works for now. If multiple time zone support is to follow, this must be enhanced. -->
        <mass-create-edit-modal :pass-in-mass="mass" trigger-class="" trigger-icon="create"
                                trigger-text=""></mass-create-edit-modal>
      </div>
      <div class="fst-italic">{{ mass.annotation }}</div>
      <ul>
        <li :style="{color: getColor(mass.requiredAltarServers - this.getNumberOfAltarServers())}">
          {{ getNumberOfAltarServers() }} / {{ mass.requiredAltarServers }} Messdiener
        </li>
        <li :style="{color: getColor(mass.requiredLeaders - this.getNumberOfLeaders())}">{{ getNumberOfLeaders() }} /
          {{ mass.requiredLeaders }} Leiter
        </li>
        <li v-if="mass.incense" :style="{color: getColor(2 - this.getNumberOfIncense())}">{{ getNumberOfIncense() }} / 2
          Weihrauch
        </li>
      </ul>
    </div>
    <div class="card-body">
      <div class="card-title d-flex justify-content-between align-items-baseline">
        <h5>Messdiener</h5>
        <div class="btn-group ms-3">
          <button :disabled="this.currentlyScheduling" class="btn btn-sm btn-warning" @click.stop="reschedule(false)">
            <em class="align-middle material-icons">refresh</em></button>
          <button :disabled="this.currentlyScheduling" class="btn btn-sm btn-success" @click.stop="reschedule(true)">
            <em class="align-middle material-icons">update</em></button>
          <button :disabled="this.currentlyScheduling" class="btn btn-sm btn-danger" @click="clearMass"><em
              class="align-middle material-icons">delete_sweep</em></button>
        </div>
      </div>
      <multiselect :loading="this.loadingProposals" :options="this.groupedAltarServerProposals" group-label="label"
                   group-values="servers" :group-select="false" @open="getAltarServerProposals"
                   @select="onSelect">
        <template v-slot:option="{ option }">
          <div v-if="option.$groupLabel" class="fw-bold text-muted">{{ option.$groupLabel }}</div>
          <div v-else-if="option.reasons" class="text-muted" :title="option.reasons.join(', ')">
            {{ this.altarServerStore.getPlannameById(option.altar_server_id) }} ⚠
          </div>
          <div v-else class="d-flex justify-content-between">
            <div
                :class="{'fw-bold': this.altarServerStore.getItemById(option.altar_server_id).leader, 'text-decoration-underline': this.altarServerStore.getItemById(option.altar_server_id).incense && mass.incense }">
              {{ this.altarServerStore.getPlannameById(option.altar_server_id) }}
            </div>
            <small>-{{ option.dist_prev_mass }} | {{ option.total_shifts }} | +{{ option.dist_next_mass }}</small>
          </div>
        </template>
      </multiselect>
      <div v-if="pendingInvalidServer" class="modal-backdrop fade show"></div>
      <div v-if="pendingInvalidServer" class="modal d-block" tabindex="-1" @click.self="cancelInvalid">
        <div class="modal-dialog">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Trotzdem einplanen?</h5>
            </div>
            <div class="modal-body" v-if="pendingInvalidServer">
              <p><strong>{{ altarServerStore.getPlannameById(pendingInvalidServer.altar_server_id) }}</strong> kann nicht eingeteilt werden, weil:</p>
              <ul>
                <li v-for="reason in pendingInvalidServer.reasons" :key="reason">{{ reason }}</li>
              </ul>
            </div>
            <div class="modal-footer">
              <button class="btn btn-secondary" @click="cancelInvalid">Abbrechen</button>
              <button class="btn btn-warning" @click="confirmInvalid">Trotzdem einplanen</button>
            </div>
          </div>
        </div>
      </div>
      <ul class="list-group">
        <li v-for="altarServer in mass.altarServers" :key="altarServer"
            :class="{
              'list-group-item-success': hoveredAltarServerId === altarServer,
              'list-group-item-danger': violations[altarServer] && hoveredAltarServerId !== altarServer
            }"
            :title="(violations[altarServer] || []).join(', ')"
            class="list-group-item d-flex justify-content-between align-items-center"
            @mouseleave="$emit('hoverAltarServer', null)" @mouseover="$emit('hoverAltarServer', altarServer)">
          <div
              :class="{'fw-bold': this.altarServerStore.getItemById(altarServer).leader, 'text-decoration-underline': this.altarServerStore.getItemById(altarServer).incense && mass.incense }">
            {{ this.altarServerStore.getPlannameById(altarServer) }}
          </div>
          <button class="btn btn-sm btn-sm btn-outline-danger"
                  @click="this.removeAltarServer(altarServer)">
            <em class="align-middle material-icons">person_remove</em></button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import MassCreateEditModal from "@/components/MassCreateEditModal";
import Multiselect from "vue-multiselect";
import {useAltarServerStore} from "@/stores/altarServer";
import {useMassStore} from "@/stores/mass";

export default {
  name: "MassDetail",
  components: {
    MassCreateEditModal,
    Multiselect
  },
  props: ['massId', 'scheduleId', 'hoveredAltarServerId', 'violations'],
  emits: ['hoverAltarServer'],
  data: function () {
    return {
      mass: {},
      currentlyScheduling: false,
      altarServerProposals: [],
      altarServerUnavailable: [],
      loadingProposals: false,
      pendingInvalidServer: null
    }
  },
  computed: {
    groupedAltarServerProposals() {
      const groups = {'Weihrauch': [], 'Leiter': [], 'Messdiener': []};
      for (const proposal of this.altarServerProposals) {
        const server = this.altarServerStore.getItemById(proposal.altar_server_id);
        if (this.mass.incense && server.incense)
          groups['Weihrauch'].push(proposal);
        else if (server.leader)
          groups['Leiter'].push(proposal);
        else
          groups['Messdiener'].push(proposal);
      }
      // Float categories whose requirement is not yet met to the top, keeping the base
      // priority order (Weihrauch > Leiter > Messdiener) as a stable tie-breaker.
      const needed = {
        'Weihrauch': this.mass.incense && this.getNumberOfIncense() < 2,
        'Leiter': this.getNumberOfLeaders() < this.mass.requiredLeaders,
        'Messdiener': this.getNumberOfAltarServers() < this.mass.requiredAltarServers
      };
      const sortedGroups = ['Weihrauch', 'Leiter', 'Messdiener']
          .sort((a, b) => needed[a] === needed[b] ? 0 : needed[a] ? -1 : 1)
          .map(label => ({label, servers: groups[label]}))
          .filter(group => group.servers.length > 0);
      if (this.altarServerUnavailable.length > 0) {
        sortedGroups.push({label: 'Nicht verfügbar', servers: this.altarServerUnavailable});
      }
      return sortedGroups;
    }
  },
  methods: {
    getColor(difference) {
      if (difference > 0)
        return 'red';
      else if (difference < 0)
        return 'green';
      else
        return '';
    },
    async getAltarServerProposals() {
      this.loadingProposals = true;
      await this.axios.get(`/messen/${this.massId}/besetzung_vorschlag/`).then((response) => {
        this.altarServerProposals = response.data.available;
        this.altarServerUnavailable = response.data.unavailable;
      }).catch((e) => {
        console.log(e);
      });
      this.loadingProposals = false;
    },
    async clearMass() {
      try {
        await this.axios.delete(`/messen/${this.massId}/besetzung/`);
        this.mass.altarServers = [];
      } catch (e) {
        console.log(e);
      }
    },
    async reschedule(preserve_current_staffing) {
      try {
        this.currentlyScheduling = true;
        if (preserve_current_staffing)
          await this.axios.put(`/messen/${this.mass.id}/besetzung/?erhalte_aktuelle_aufstellung`);
        else
          await this.axios.put(`/messen/${this.mass.id}/besetzung/`);
        await this.massStore.fetchItem(this.massId);
        this.currentlyScheduling = false;
      } catch (e) {
        console.log(e);
      }
    },
    onSelect(entry) {
      if (entry.reasons) {
        this.pendingInvalidServer = entry;
      } else {
        this.addAltarServer(entry);
      }
    },
    async confirmInvalid() {
      await this.addAltarServer(this.pendingInvalidServer);
      this.pendingInvalidServer = null;
    },
    cancelInvalid() {
      this.pendingInvalidServer = null;
    },
    async addAltarServer(altarServer) {
      this.mass.altarServers.push(altarServer.altar_server_id);
      await this.massStore.updateItem(this.mass);
    },
    async removeAltarServer(altarServer) {
      this.mass.altarServers.splice(this.mass.altarServers.indexOf(altarServer), 1);
      await this.massStore.updateItem(this.mass);
    },
    getNumberOfAltarServers() {
      if (Array.isArray(this.mass.altarServers))
        return this.mass.altarServers.length;
    },
    getNumberOfLeaders() {
      if (Array.isArray(this.mass.altarServers))
        return this.mass.altarServers.filter(a => this.altarServerStore.getItemById(a).leader).length;
    },
    getNumberOfIncense() {
      if (Array.isArray(this.mass.altarServers))
        return this.mass.altarServers.filter(a => this.altarServerStore.getItemById(a).incense).length;
    },
  },
  created() {
    if (!this.altarServerStore.loadedData)
      this.altarServerStore.getData();
    if (!this.massStore.loadedData)
      this.massStore.getData();

    // Load mass from mass store
    this.mass = this.massStore.getItemById(this.massId);

    // Reload mass when some mass changed (this ensures reactivity but does not trigger HTTP requests)
    this.massStore.$subscribe(() => {
      this.mass = this.massStore.getItemById(this.massId)
    })
  },
  setup() {
    const altarServerStore = useAltarServerStore();
    const massStore = useMassStore();
    return {altarServerStore, massStore};
  }
}
</script>

<style scoped>
h5 {
  font-size: 1rem;
}
</style>